import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    CircularProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import EquipmentService from "../../../services/Equipment.service";
import { formatDate } from "./period";
import ResolveTaggedOutModal from "./ResolveTaggedOutModal";
import EditTaggedOutDescriptionDialog from "./EditTaggedOutDescriptionDialog";
import { useAuth } from "../../../contexts/AuthContext";

function SectionHeader({ title }) {
    return (
        <Typography
            variant="caption"
            sx={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "text.secondary",
                display: "block",
                mb: 0.5,
            }}
        >
            {title}
        </Typography>
    );
}

function InfoRow({ label, value }) {
    if (value === null || value === undefined || value === "") return null;
    return (
        <Box sx={{ mb: 1.25 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
            >
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                {value}
            </Typography>
        </Box>
    );
}

export default function EquipmentDetailsDialog({ open, onClose, equipment }) {
    const { user } = useAuth();
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [logOpen, setLogOpen] = useState(false);
    const [resolveTarget, setResolveTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);

    useEffect(() => {
        if (!open || !equipment) return;
        setLoading(true);
        EquipmentService.listIssues({ equipment: equipment.id })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : res.data.results || [];
                setIssues(list);
            })
            .finally(() => setLoading(false));
    }, [open, equipment]);

    if (!equipment) return null;

    const isTaggedOut = issues.some((i) => !i.is_resolved);

    const refreshIssue = (updated) => {
        setIssues((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    };

    return (
        <Dialog fullScreen open={open} onClose={onClose} scroll="paper">
            <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Stack>
                        <Typography variant="h6" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                            {equipment.nickname}
                        </Typography>
                        <Typography variant="subtitle2" color={isTaggedOut ? "error.main" : "success.main"}>
                            {isTaggedOut ? "Tagged Out" : "In Service"}
                        </Typography>
                    </Stack>
                    <IconButton edge="end" aria-label="close" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <SectionHeader title="Specifications" />
                <InfoRow label="Manager" value={equipment.manager_detail ? equipment.manager_detail.full_name || equipment.manager_detail.username : null} />
                <InfoRow label="Make" value={equipment.make} />
                <InfoRow label="Model" value={equipment.model} />
                <InfoRow label="Year" value={equipment.year} />
                <InfoRow label="Color" value={equipment.color} />
                <InfoRow label="Weight Capacity" value={equipment.weight_capacity} />
                <InfoRow label="Electric" value={equipment.is_electric ? "Yes" : "No"} />
                {(equipment.attribute_values_detail || []).map((attr) => (
                    <InfoRow key={attr.id} label={attr.definition_detail?.label} value={attr.value_display} />
                ))}

                {equipment.documents_detail && equipment.documents_detail.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <SectionHeader title="Documents" />
                        {equipment.documents_detail.map((doc) => (
                            <Typography key={doc.id} variant="body2" sx={{ mb: 0.5 }}>
                                {doc.document_url ? (
                                    <a href={doc.document_url} target="_blank" rel="noreferrer">
                                        {doc.title}
                                    </a>
                                ) : (
                                    doc.title
                                )}
                            </Typography>
                        ))}
                    </>
                )}

                <Divider sx={{ my: 2 }} />
                <List dense disablePadding sx={{ bgcolor: "background.paper", border: 1, borderColor: isTaggedOut ? "error.main" : "success.main" }}>
                    <ListItemButton onClick={() => setLogOpen((v) => !v)}>
                        <ListItemText primary="Equipment Tag Out Log" />
                        {logOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={logOpen} timeout="auto" unmountOnExit>
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : issues.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                No issues recorded.
                            </Typography>
                        ) : (
                            <List component="div" disablePadding>
                                {issues.map((issueItem) => {
                                    const isEditable = issueItem.created_by_detail?.id === user?.id;
                                    return (
                                        <ListItem key={issueItem.id} divider sx={{ pl: 2 }}>
                                            <ListItemIcon
                                                onClick={() => !issueItem.is_resolved && setResolveTarget(issueItem)}
                                                sx={{ cursor: issueItem.is_resolved ? "default" : "pointer" }}
                                            >
                                                {issueItem.is_resolved ? (
                                                    <CheckIcon color="success" />
                                                ) : (
                                                    <WarningIcon color="error" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={issueItem.description || "No description provided"}
                                                secondary={
                                                    <>
                                                        {issueItem.created_by_detail
                                                            ? `${issueItem.created_by_detail.full_name || issueItem.created_by_detail.username} · `
                                                            : ""}
                                                        {formatDate(issueItem.date)}
                                                        {issueItem.is_resolved && issueItem.resolved_by_detail
                                                            ? ` · Resolved by ${issueItem.resolved_by_detail.full_name || issueItem.resolved_by_detail.username}`
                                                            : ""}
                                                    </>
                                                }
                                            />
                                            {isEditable && (
                                                <IconButton size="small" onClick={() => setEditTarget(issueItem)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </Collapse>
                </List>
            </DialogContent>

            <ResolveTaggedOutModal
                open={Boolean(resolveTarget)}
                onClose={() => setResolveTarget(null)}
                equipment={equipment}
                issue={resolveTarget}
                onResolved={refreshIssue}
            />
            <EditTaggedOutDescriptionDialog
                open={Boolean(editTarget)}
                onClose={() => setEditTarget(null)}
                issue={editTarget}
                onUpdated={refreshIssue}
            />
        </Dialog>
    );
}
